import api from "./api";

// --- Types ---

export type HospitalStatus = "ACTIVE" | "INACTIVE";
export type OnboardingStatus = "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";

export type EmailNotificationStatus = "PENDING" | "SENT" | "FAILED" | "RETRY";

export interface EmailContent {
  subject?: string;
  html?: string;
  text?: string;
}

export interface EmailNotification {
  id: string;
  hospitalId: string;
  type: string;
  recipientEmail: string;
  subject?: string;
  status: EmailNotificationStatus;
  errorMessage?: string;
  attemptCount?: number;
  sentAt?: string;
  emailContent?: EmailContent | null;
  createdAt?: string;
  updatedAt?: string;
}

export const HOSPITAL_TYPES = [
  "General",
  "Specialty",
  "Teaching",
  "Clinic",
  "Diagnostic",
  "Rehabilitation",
  "Other",
] as const;

export type HospitalType = (typeof HOSPITAL_TYPES)[number];

export const TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Singapore",
  "Asia/Kolkata",
  "Australia/Sydney",
  "Pacific/Auckland",
] as const;

export interface Hospital {
  id: string;
  hospitalName: string;
  hospitalCode?: string;
  registrationNumber?: string;
  hospitalType?: string;
  domainName: string;
  officialEmail: string;
  officialPhone: string;
  dbName: string;
  addressline1?: string;
  addressline2?: string;
  city?: string;
  state?: string;
  country?: string;
  postcode?: string;
  timezone?: string;
  contactPersonName?: string;
  contactPersonEmail?: string;
  contactPersonPhone?: string;
  status: HospitalStatus;
  onboardingStatus: OnboardingStatus;
  subscriptionPlanId?: string;
  logoUrl?: string;
  notes?: string;
  isSubscribed?: boolean;
  otpVerificationEnabled?: boolean;
  emailNotification?: EmailNotification | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface HospitalListResponse {
  hospitals: Hospital[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface HospitalListParams {
  search?: string;
  status?: HospitalStatus;
  onboardingStatus?: OnboardingStatus;
  city?: string;
  country?: string;
  hospitalType?: string;
  createdFrom?: string;
  createdTo?: string;
  page?: number;
  limit?: number;
}

export interface CreateHospitalPayload {
  hospitalName: string;
  hospitalCode: string;
  registrationNumber?: string;
  hospitalType?: string;
  domainName: string;
  officialEmail: string;
  officialPhone: string;
  addressline1?: string;
  addressline2?: string;
  city?: string;
  state?: string;
  country?: string;
  postcode?: string;
  timezone?: string;
  subscriptionPlanId?: string;
  contactPersonName?: string;
  contactPersonEmail?: string;
  contactPersonPhone?: string;
}

export type UpdateHospitalPayload = Partial<CreateHospitalPayload> & {
  status?: HospitalStatus;
  onboardingStatus?: OnboardingStatus;
  logoUrl?: string;
  notes?: string;
  otpVerificationEnabled?: boolean;
};

// --- Dashboard statistics ---

export interface HospitalStats {
  total: number;
  active: number;
  inProgress: number;
  inactive: number;
}

export interface DoctorStats {
  total: number;
  active: number;
  inProgress: number;
  inactive: number;
}

export interface DashboardStats {
  hospitals: HospitalStats;
  doctors: DoctorStats;
}

export const getDashboardStats = async (): Promise<DashboardStats> => {
  const { data } = await api.get("/api/super-admin/dashboard/stats");
  return data.data;
};

// --- Onboarding trend (dashboard line charts) ---

export type TrendGranularity = "month" | "week" | "day";

export interface OnboardingTrendPoint {
  /** Bucket-start label: "YYYY-MM" for months, "YYYY-MM-DD" for weeks/days */
  month: string;
  count: number;
}

export interface OnboardingTrend {
  hospitals: OnboardingTrendPoint[];
  doctors: OnboardingTrendPoint[];
  granularity: TrendGranularity;
}

export const getOnboardingTrend = async (
  params?: { granularity?: TrendGranularity; startDate?: string; endDate?: string }
): Promise<OnboardingTrend> => {
  const { data } = await api.get("/api/super-admin/dashboard/onboarding-trend", { params });
  return data.data;
};

// --- Doctor listing ---

export type DoctorStatus = "active" | "in_progress" | "inactive";

export interface Doctor {
  id: number;
  doctorId: number | null;
  name: string;
  email: string;
  active: boolean;
  status: DoctorStatus;
  hospitalId: string;
  hospitalName: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  medicalRegistrationNumber?: string;
  specialization?: string;
  department?: string;
  qualification?: string;
  yearsOfExperience?: number;
  licenseNumber?: string;
  subscriptionPlanId?: string | null;
  subscriptionPlanName?: string | null;
  subscriptionPlanPrice?: number | string | null;
  subscriptionPlanCurrency?: string | null;
  subscriptionPlanIsActive?: boolean | null;
  onboardingStatus?: string;
}

export interface DoctorListResponse {
  doctors: Doctor[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface DoctorListParams {
  search?: string;
  status?: DoctorStatus;
  onboarding?: string;
  specialization?: string;
  department?: string;
  qualification?: string;
  createdFrom?: string;
  createdTo?: string;
  page?: number;
  limit?: number;
}

export interface DoctorFilterOptions {
  specializations: string[];
  departments: string[];
  qualifications: string[];
}

export const listDoctors = async (params?: DoctorListParams): Promise<DoctorListResponse> => {
  const { data } = await api.get("/api/super-admin/doctors", { params });
  return data.data;
};

export const getDoctorFilterOptions = async (): Promise<DoctorFilterOptions> => {
  const { data } = await api.get("/api/super-admin/doctors/filters");
  return data.data;
};

// --- Doctor Onboarding ---

export interface CreateDoctorPayload {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  medicalRegistrationNumber: string;
  specialization: string;
  hospitalId: number;
  department: string;
  qualification: string;
  yearsOfExperience: number;
  licenseNumber: string;
  status: "Active" | "Inactive";
  onboardingStatus?: "not started" | "in progress" | "completed";
  subscriptionPlanId?: string;
}

export interface HospitalDropdown {
  id: number;
  hospitalName: string;
}

export const createDoctor = async (payload: CreateDoctorPayload): Promise<any> => {
  const { data } = await api.post("/api/super-admin/doctors", payload);
  return data.data;
};

export const getHospitalsForDropdown = async (): Promise<HospitalDropdown[]> => {
  const { data } = await api.get("/api/super-admin/hospitals/dropdown");
  return data.data;
};

// --- Doctor Detail (single doctor) ---

export const getDoctor = async (userId: number): Promise<Doctor> => {
  const { data } = await api.get(`/api/super-admin/doctors/${userId}`);
  return data.data;
};

export const updateDoctor = async (userId: number, payload: Partial<CreateDoctorPayload>): Promise<Doctor> => {
  const { data } = await api.put(`/api/super-admin/doctors/${userId}`, payload);
  return data.data;
};

export const deleteDoctor = async (userId: number): Promise<void> => {
  await api.delete(`/api/super-admin/doctors/${userId}`);
};

// --- Legacy functions (kept for backward compatibility) ---

export const getAllHospitals = async () => {
  const { data } = await api.get("/api/hospital");
  return data.data;
};

export const getHospitalById = async (id: string) => {
  const res = await api.get(`/api/hospital/${id}`);
  return res.data.data;
};

interface UpdateHospitalStatusPayload {
  status: HospitalStatus;
}

export const updateHospitalStatus = async (hospitalId: string, payload: UpdateHospitalStatusPayload) => {
  const res = await api.patch(`/api/hospital/${hospitalId}/status`, payload);
  return res.data;
};

// --- New CRUD functions (Super Admin Hospital Management) ---

const BASE = "/api/super-admin/hospitals";

export const listHospitals = async (params?: HospitalListParams): Promise<HospitalListResponse> => {
  const { data } = await api.get(BASE, { params });
  return data.data;
};

export const getHospitalTypes = async (): Promise<string[]> => {
  const { data } = await api.get(`${BASE}/types`);
  return data.data.hospitalTypes;
};

export const getHospital = async (id: string): Promise<Hospital> => {
  const { data } = await api.get(`${BASE}/${id}`);
  return data.data;
};

export const createHospital = async (payload: CreateHospitalPayload): Promise<Hospital> => {
  const { data } = await api.post(BASE, payload);
  return data.data;
};

export const updateHospital = async (id: string, payload: UpdateHospitalPayload): Promise<Hospital> => {
  const { data } = await api.put(`${BASE}/${id}`, payload);
  return data.data;
};

export const deleteHospital = async (id: string): Promise<void> => {
  await api.delete(`${BASE}/${id}`);
};

export const getHospitalEmailNotifications = async (id: string): Promise<EmailNotification[]> => {
  const { data } = await api.get(`${BASE}/${id}/email-notifications`);
  return data.data;
};

export const diagnoseEmail = async (): Promise<any> => {
  const { data } = await api.get(`${BASE}/diagnose-email`);
  return data.data;
};
